import type { SubtitleEditorData } from '@/components/SubtitleEditor';
import type { MediaLayer } from '@/lib/layers/types';
import type { VideoEffects } from '@/lib/video-effects/types';
import type { KeyframeTransition, SceneTransition } from '@/lib/transitions/types';
import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import type { ClipCoverImage } from '@/lib/cover/coverImage';
import { resolveSceneCropTarget } from '@/lib/crop/sceneCropFraming';
import type { LayerRect } from '@/lib/layers/types';
import type { LayoutCatalogEntry } from './layoutCatalog';
import { isInsideRange } from './cameraMove';
import { sanitizeMotionScene } from '@/lib/motion/sanitizeMotionScene';
import { isSfxLayer } from './soundEffects';

function motionSummary(raw: unknown) {
  const { scene } = sanitizeMotionScene(raw);
  return scene ? { elements: scene.elements.map((element) => ({ id: element.id, type: element.type })) }
    : { elements: [], scene_invalid: true };
}

// What the assistant sees of the open editor, sent with each message and after
// each batch of edits. Times are seconds on the clip timeline. Kept small: it
// rides along every model call of the turn.

export interface TranscriptWord {
  start: number;
  end: number;
  word?: string;
  punctuated_word?: string;
}

export interface TimelineSpan {
  startTime: number;
  endTime: number;
}

const MAX_TRANSCRIPT_CHARS = 6000;
const MAX_LINE_WORDS = 10;
const LINE_GAP_SECONDS = 0.7;
const round = (value: number) => Math.round(value * 10) / 10;
const round2 = (value: number) => Math.round(value * 100) / 100;
const MIN_SILENCE_SECONDS = 0.6;
const MAX_SILENCES = 60;
const CONTEXT_SECONDS = 20;
const MAX_CONTEXT_CHARS = 1500;

const tokenOf = (word: TranscriptWord) => (word.punctuated_word || word.word || '').trim();

/** Timed words as short lines (a pause, 10 words or a sentence end breaks). */
function toLines(timed: Array<{ at: number; token: string }>, maxChars: number) {
  const lines: Array<{ t: number; text: string; words: number; last: number }> = [];
  let chars = 0;
  timed.forEach(({ at, token }) => {
    if (!token || chars > maxChars) return;
    const current = lines[lines.length - 1];
    const breaks = !current
      || current.words >= MAX_LINE_WORDS
      || at - current.last > LINE_GAP_SECONDS
      || /[.!?]$/.test(current.text);
    if (breaks) {
      lines.push({ t: round(at), text: token, words: 1, last: at });
    } else {
      current.text = `${current.text} ${token}`;
      current.words += 1;
      current.last = at;
    }
    chars += token.length + 1;
  });
  return lines.map(({ t, text }) => ({ t, text }));
}

/** Pauses (no word for 0.6 s or more) on the clip timeline: what
 *  editor_cut_ranges removes to "cut the pauses". */
export function buildClipSilences(
  words: readonly TranscriptWord[],
  intervals: readonly TimelineSpan[],
): Array<{ start: number; end: number }> {
  const spoken: Array<{ start: number; end: number }> = [];
  let offset = 0;
  intervals.forEach((interval) => {
    words.forEach((word) => {
      if (!Number.isFinite(word.start) || word.start < interval.startTime || word.start >= interval.endTime) return;
      const end = Number.isFinite(word.end) ? Math.min(word.end, interval.endTime) : word.start;
      spoken.push({ start: offset + (word.start - interval.startTime), end: offset + (end - interval.startTime) });
    });
    offset += interval.endTime - interval.startTime;
  });
  spoken.sort((a, b) => a.start - b.start);
  const silences: Array<{ start: number; end: number }> = [];
  let cursor = 0;
  const push = (start: number, end: number) => {
    if (end - start >= MIN_SILENCE_SECONDS - 1e-9 && silences.length < MAX_SILENCES) {
      silences.push({ start: round2(start), end: round2(end) });
    }
  };
  spoken.forEach((word) => {
    push(cursor, word.start);
    cursor = Math.max(cursor, word.end);
  });
  push(cursor, offset);
  return silences;
}

/** What is said up to `seconds` before and after the clip (outside the first
 *  and last scenes), timed from the clip start: before is negative, after
 *  counts on from the clip end. For editor_extend. */
export function buildTranscriptContext(
  words: readonly TranscriptWord[],
  intervals: readonly TimelineSpan[],
  seconds = CONTEXT_SECONDS,
): { before: Array<{ t: number; text: string }>; after: Array<{ t: number; text: string }> } {
  if (!intervals.length) return { before: [], after: [] };
  const first = intervals[0];
  const last = intervals[intervals.length - 1];
  const clipDuration = intervals.reduce((total, interval) => total + (interval.endTime - interval.startTime), 0);
  const sorted = [...words].filter((word) => Number.isFinite(word.start)).sort((a, b) => a.start - b.start);
  const before = sorted
    .filter((word) => word.start >= first.startTime - seconds && word.start < first.startTime)
    .map((word) => ({ at: word.start - first.startTime, token: tokenOf(word) }));
  const after = sorted
    .filter((word) => word.start >= last.endTime && word.start <= last.endTime + seconds)
    .map((word) => ({ at: clipDuration + (word.start - last.endTime), token: tokenOf(word) }));
  return { before: toLines(before, MAX_CONTEXT_CHARS), after: toLines(after, MAX_CONTEXT_CHARS) };
}

/** How a visual layer sits in the frame: fill, fit, or its rect. */
function placementOf(rect: LayerRect): { placement: string; rect?: LayerRect } {
  const covers = rect.x <= 0.01 && rect.y <= 0.01 && rect.x + rect.w >= 0.99 && rect.y + rect.h >= 0.99;
  if (covers) return { placement: 'fill' };
  const inside = rect.x >= -0.01 && rect.y >= -0.01 && rect.x + rect.w <= 1.01 && rect.y + rect.h <= 1.01;
  if (inside && (rect.w >= 0.99 || rect.h >= 0.99)) return { placement: 'fit' };
  return {
    placement: 'rect',
    rect: { x: round2(rect.x), y: round2(rect.y), w: round2(rect.w), h: round2(rect.h) },
  };
}

const CENTERED_FRAMING = { x: 50, y: 50, zoom: 1 };

function framingOf(keyframes: readonly CropCenterKeyframe[], scene: TimelineSpan, time = scene.startTime) {
  if (!keyframes.length) return CENTERED_FRAMING;
  // At the scene start, the keyframe starting there — not the previous
  // shot's, which ends at the same cut instant.
  const keyframe = resolveSceneCropTarget([...keyframes], [scene], time)?.keyframe;
  return keyframe
    ? { x: round(keyframe.x), y: round(keyframe.y), zoom: keyframe.zoom ?? 1 }
    : CENTERED_FRAMING;
}

/** The clip's spoken words as short timed lines on the clip timeline. Words
 *  outside the kept intervals are cut from the clip and left out. */
export function buildClipTranscript(
  words: readonly TranscriptWord[],
  intervals: readonly TimelineSpan[],
): Array<{ t: number; text: string }> {
  const timed: Array<{ at: number; token: string }> = [];
  let offset = 0;
  intervals.forEach((interval) => {
    words.forEach((word) => {
      if (!Number.isFinite(word.start) || word.start < interval.startTime || word.start >= interval.endTime) return;
      timed.push({ at: offset + (word.start - interval.startTime), token: tokenOf(word) });
    });
    offset += interval.endTime - interval.startTime;
  });
  return toLines(timed, MAX_TRANSCRIPT_CHARS);
}

export interface EditorPageStateInput {
  clip: { title: string | null; index: number; duration: number; playhead: number; aspect: 'vertical' | 'horizontal' };
  clips: Array<{ title: string | null }>;
  transcript: Array<{ t: number; text: string }>;
  scenes: readonly TimelineSpan[];
  subtitle: SubtitleEditorData;
  subtitlesEnabled: boolean;
  fontName: string | null;
  layers: readonly MediaLayer[];
  videoEffects: VideoEffects;
  transitions: readonly SceneTransition[];
  templateName: string | null;
  fonts: readonly string[];
  templates: ReadonlyArray<{ id: string; name: string }>;
  pro: boolean;
  /** The preview crops the source: scenes carry their framing. */
  framingAvailable?: boolean;
  cropKeyframes?: readonly CropCenterKeyframe[];
  /** Camera moves (glides at framing boundaries inside scenes). */
  keyframeTransitions?: readonly KeyframeTransition[];
  silences?: Array<{ start: number; end: number }>;
  context?: { before: Array<{ t: number; text: string }>; after: Array<{ t: number; text: string }> };
  layout?: { kind: string; preset?: string | null } | null;
  layoutCatalog?: readonly LayoutCatalogEntry[];
  cover?: ClipCoverImage | null;
}

export function buildEditorPageState(input: EditorPageStateInput): Record<string, unknown> {
  let at = 0;
  const scenes = input.scenes.map((scene, index) => {
    const start = at;
    at += scene.endTime - scene.startTime;
    return {
      index,
      start: round(start),
      end: round(at),
      source_start: round2(scene.startTime),
      source_end: round2(scene.endTime),
      ...(input.framingAvailable ? { framing: framingOf(input.cropKeyframes ?? [], scene) } : {}),
      // Where a camera move inside the scene ends (editor_camera_move).
      ...(input.framingAvailable && (input.keyframeTransitions ?? []).some((transition) => (
        transition.kind === 'camera-move' && isInsideRange(transition, { start: scene.startTime, end: scene.endTime })
      )) ? { camera_move: { to: framingOf(input.cropKeyframes ?? [], scene, scene.endTime - 0.001) } } : {}),
    };
  });
  const { subtitle } = input;
  const look = input.videoEffects;
  const adjustments = Object.fromEntries(
    (['exposure', 'contrast', 'saturation', 'temperature', 'vibrance', 'vignette', 'grain', 'grayscale'] as const)
      .filter((key) => look[key] !== 0)
      .map((key) => [key, look[key]]),
  );
  return {
    kind: 'editor',
    clip: {
      title: input.clip.title,
      index: input.clip.index,
      duration: round(input.clip.duration),
      playhead: round(input.clip.playhead),
      format: input.clip.aspect,
    },
    clips: input.clips.slice(0, 20).map((clip, index) => ({ index: index + 1, title: clip.title })),
    transcript: input.transcript,
    silences: input.silences ?? [],
    context_before: input.context?.before ?? [],
    context_after: input.context?.after ?? [],
    scenes,
    subtitles: {
      enabled: input.subtitlesEnabled,
      template: input.templateName,
      font: input.fontName,
      size: subtitle.fontSize,
      text_color: subtitle.textColor,
      highlight: subtitle.highlightEnabled ? subtitle.highlightColor : null,
      highlight_box: subtitle.highlightBoxEnabled ? subtitle.highlightBoxBackgroundColor : null,
      animation: subtitle.animation,
      position: subtitle.position,
      text_transform: subtitle.textTransform,
      outline: subtitle.strokeEnabled ? { color: subtitle.strokeColor, width: subtitle.strokeWidth } : null,
      background: subtitle.backgroundEnabled ? subtitle.backgroundColor : null,
    },
    texts: input.layers
      .filter((layer) => layer.type === 'text')
      .map((layer) => ({
        id: layer.id,
        text: layer.type === 'text' ? layer.text : '',
        start: round(layer.start),
        end: round(layer.end),
      })),
    other_layers: input.layers
      .filter((layer) => layer.type !== 'text' && !isSfxLayer(layer))
      .map((layer) => ({
        id: layer.id,
        type: layer.type,
        name: layer.name,
        start: round(layer.start),
        end: round(layer.end),
        ...(layer.opacity !== 1 ? { opacity: Math.round(layer.opacity * 100) } : {}),
        ...(layer.type === 'video' || layer.type === 'audio'
          ? { volume: Math.round(layer.volume * 100), speed: layer.speed }
          : {}),
        ...(layer.type === 'image' || layer.type === 'video' || layer.type === 'motion' ? placementOf(layer.rect) : {}),
        ...(layer.type === 'motion' ? motionSummary(layer.scene) : {}),
      })),
    // Always sent (even empty): it tells the backend this editor supports
    // sound effects.
    sound_effects: input.layers.filter(isSfxLayer).map((layer) => ({
      id: layer.id,
      sfx: layer.sfxId,
      name: layer.name,
      start: round(layer.start),
      end: round(layer.end),
      volume: Math.round(layer.volume * 100),
    })),
    effects: (look.sections ?? []).map((section) => ({
      id: section.id,
      effect: section.effect,
      start: round(section.start),
      end: round(section.end),
      intensity: section.intensity,
    })),
    transitions: input.transitions.map((transition) => ({
      after_scene: transition.afterSceneIndex,
      kind: transition.kind,
      duration: transition.durationSec,
    })),
    look: {
      preset: look.lut ? (look.lut.preset === 'custom' ? 'custom' : look.lut.preset) : null,
      strength: look.lut ? Math.round(look.lut.strength * 100) : null,
      ...adjustments,
    },
    layout: input.layout ? { kind: input.layout.kind, preset: input.layout.preset ?? null } : null,
    cover: input.cover ? { set: true, source: input.cover.source } : { set: false },
    plan: { pro: input.pro, note: input.pro ? null : 'Exporting texts, effects and transitions needs PRO' },
    options: {
      fonts: input.fonts.slice(0, 150),
      templates: input.templates.slice(0, 40),
      layouts: (input.layoutCatalog ?? []).map((entry) => ({
        kind: entry.kind,
        ...(entry.presets.length ? { presets: entry.presets } : {}),
        fields: entry.fields.map((field) => ({
          key: field.key,
          type: field.type,
          ...(field.options ? { options: field.options } : {}),
          ...(field.min !== undefined ? { min: field.min, max: field.max } : {}),
        })),
      })),
    },
  };
}

type PageState = Record<string, unknown>;

const sizeOf = (state: PageState) => JSON.stringify(state).length;
const listOf = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Steps that shrink the page state, least useful first. Each returns the
 *  state with one part cut back. */
const TRIM_STEPS: Array<{ label: string; trim: (state: PageState) => PageState }> = [
  { label: 'context', trim: (state) => ({ ...state, context_before: [], context_after: [] }) },
  {
    label: 'options',
    trim: (state) => {
      const options = (state.options ?? {}) as Record<string, unknown>;
      return {
        ...state,
        options: { ...options, fonts: listOf(options.fonts).slice(0, 40), templates: listOf(options.templates).slice(0, 15) },
      };
    },
  },
  {
    label: 'motion_elements',
    trim: (state) => ({ ...state, other_layers: listOf(state.other_layers).map((layer) => {
      const item = layer as Record<string, unknown>;
      if (!Array.isArray(item.elements) || item.elements.length <= 10) return item;
      return { ...item, elements: item.elements.slice(0, 10), elements_total: item.elements.length };
    }) }),
  },
  { label: 'silences', trim: (state) => ({ ...state, silences: listOf(state.silences).slice(0, 25) }) },
  { label: 'clips', trim: (state) => ({ ...state, clips: listOf(state.clips).slice(0, 8) }) },
  { label: 'transcript', trim: (state) => ({ ...state, transcript: listOf(state.transcript).slice(0, 180) }) },
  { label: 'scenes', trim: (state) => ({ ...state, scenes: listOf(state.scenes).slice(0, 80) }) },
  { label: 'transcript', trim: (state) => ({ ...state, transcript: listOf(state.transcript).slice(0, 60) }) },
  { label: 'scenes', trim: (state) => ({ ...state, scenes: listOf(state.scenes).slice(0, 40) }) },
  {
    label: 'layers',
    trim: (state) => ({
      ...state,
      texts: listOf(state.texts).slice(0, 20),
      other_layers: listOf(state.other_layers).slice(0, 20),
      sound_effects: listOf(state.sound_effects).slice(0, 30),
      effects: listOf(state.effects).slice(0, 20),
    }),
  },
  { label: 'transcript', trim: (state) => ({ ...state, transcript: [] }) },
];

/** The page state within the backend's size limit (a bigger one is dropped
 *  whole, leaving the model with nothing). Parts are cut back least useful
 *  first; `trimmed` names them so the model knows the lists are partial. */
export function fitPageState(state: PageState, maxChars: number): PageState {
  if (sizeOf(state) <= maxChars) return state;
  let current = state;
  const trimmed: string[] = [];
  for (const step of TRIM_STEPS) {
    current = step.trim(current);
    if (!trimmed.includes(step.label)) trimmed.push(step.label);
    if (sizeOf({ ...current, trimmed }) <= maxChars) break;
  }
  if (sizeOf({ ...current, trimmed }) <= maxChars) return { ...current, trimmed };
  // Preserve layer IDs for get/update/remove calls when lengthy text and names
  // exhaust the ordinary trimming steps. Never let the backend drop all state.
  const summary = (value: unknown) => {
    const item = value as Record<string, unknown>;
    return Object.fromEntries(['id', 'type', 'name', 'start', 'end', 'scene_invalid', 'sfx'].flatMap((key) => {
      const value = item[key];
      return value === undefined ? [] : [[key, typeof value === 'string' ? value.slice(0, 120) : value]];
    }));
  };
  const minimal = {
    kind: 'editor',
    clip: current.clip,
    texts: listOf(current.texts).map(summary),
    other_layers: listOf(current.other_layers).map(summary),
    sound_effects: listOf(current.sound_effects).map(summary),
    trimmed: [...trimmed, 'details'],
  };
  if (sizeOf(minimal) <= maxChars) return minimal;
  return maxChars >= 39 ? { kind: 'editor', trimmed: ['details'] } : {};
}
